import React, { useState, useEffect, useRef } from 'react'
import { getServerSessionId } from '../../lib/serverSession'
import { notifySponsorsOfCommissionTransfer } from '../../lib/services/sponsorChangeNotificationService'
import { filterAndRank } from '../../lib/searchRelevance'

interface User {
  id: number
  username: string
  email: string
  full_name: string
  is_active: boolean
}

interface ReferralRelationship {
  id: string
  referrer_id: number
  referred_id: number
  referral_code: string
  commission_rate: number
  total_commission: number
  status: string
  created_at: string
  referrer_username?: string
  referrer_full_name?: string
  referred_username?: string
  referred_full_name?: string
}

interface SponsorManagerProps {
  currentUser?: any
}

export const SponsorManager: React.FC<SponsorManagerProps> = ({ currentUser }) => {
  const [users, setUsers] = useState<User[]>([])
  const [referrals, setReferrals] = useState<ReferralRelationship[]>([])
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [selectedSponsor, setSelectedSponsor] = useState<User | null>(null)
  const [changeReason, setChangeReason] = useState('')
  const [loading, setLoading] = useState(false)
  const [userSearchTerm, setUserSearchTerm] = useState('')
  const [sponsorSearchTerm, setSponsorSearchTerm] = useState('')
  const [showUserDropdown, setShowUserDropdown] = useState(false)
  const [showSponsorDropdown, setShowSponsorDropdown] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info' | 'warning', text: string } | null>(null)
  const [purchaseInfo, setPurchaseInfo] = useState<{
    hasPurchases: boolean
    purchaseCount: number
    totalUsdt: number
    totalShares: number
    commissionsToOtherSponsors: number
    otherSponsorNames: { id: number; username: string }[]
    currentSponsorId: number | null
  } | null>(null)
  const [purchaseCheckLoading, setPurchaseCheckLoading] = useState(false)

  const userDropdownRef = useRef<HTMLDivElement>(null)
  const sponsorDropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    loadUsers()
    loadReferrals()
  }, [])

  // Handle clicks outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false)
      }
      if (sponsorDropdownRef.current && !sponsorDropdownRef.current.contains(event.target as Node)) {
        setShowSponsorDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const loadUsers = async () => {
    try {
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/sponsor', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'load_users' })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)
      setUsers(result.users)
    } catch (error) {
      console.error('Error loading users:', error)
      setMessage({ type: 'error', text: 'Failed to load users' })
    }
  }

  const loadReferrals = async () => {
    try {
      console.log('📥 Loading referrals...')
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/sponsor', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'load_referrals' })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)

      console.log(`   ✅ Loaded ${result.referrals.length} active referrals`)
      setReferrals(result.referrals)
    } catch (error) {
      console.error('Error loading referrals:', error)
      setMessage({ type: 'error', text: 'Failed to load referral relationships' })
    }
  }

  const checkUserPurchases = async (userId: number) => {
    setPurchaseCheckLoading(true)
    setPurchaseInfo(null)
    try {
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/sponsor', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'check_user_purchases', data: { userId } })
      })
      const result = await response.json()
      if (result.success) {
        setPurchaseInfo(result)
      }
    } catch (error) {
      console.error('Error checking user purchases:', error)
    } finally {
      setPurchaseCheckLoading(false)
    }
  }

  const getCurrentSponsor = (userId: number) => {
    return referrals.find(ref => ref.referred_id === userId && ref.status === 'active')
  }

  // Format user display text
  const formatUserDisplay = (user: User) => {
    return `${user.username} - ${user.full_name || 'No Name'} (ID: ${user.id}) - ${user.email}`
  }

  // Filter users for user selection dropdown
  const getFilteredUsers = () => {
    return filterAndRank(
      users,
      userSearchTerm,
      (u) => ({ primary: [u.username, u.email, u.full_name] }),
    )
  }

  // Filter users for sponsor selection dropdown (exclude selected user)
  const getFilteredSponsors = () => {
    return filterAndRank(
      users.filter((u) => u.id !== selectedUser?.id), // Prevent self-sponsoring
      sponsorSearchTerm,
      (u) => ({ primary: [u.username, u.email, u.full_name] }),
    )
  }

  // Handle user selection
  const handleUserSelect = (user: User) => {
    setSelectedUser(user)
    setUserSearchTerm(formatUserDisplay(user))
    setShowUserDropdown(false)
    // Reset sponsor selection when user changes
    setSelectedSponsor(null)
    setSponsorSearchTerm('')
    // Check purchase history for warnings
    checkUserPurchases(user.id)
  }

  // Handle sponsor selection
  const handleSponsorSelect = (sponsor: User) => {
    setSelectedSponsor(sponsor)
    setSponsorSearchTerm(formatUserDisplay(sponsor))
    setShowSponsorDropdown(false)
  }

  const handleChangeSponsor = async () => {
    if (!selectedUser || !selectedSponsor) {
      setMessage({ type: 'error', text: 'Please select both a user and a sponsor' })
      return
    }

    setLoading(true)
    setMessage(null)

    try {
      // Validate sponsor change
      if (selectedUser.id === selectedSponsor.id) {
        throw new Error('User cannot sponsor themselves')
      }

      const currentSponsor = getCurrentSponsor(selectedUser.id)
      if (currentSponsor && currentSponsor.referrer_id === selectedSponsor.id) {
        throw new Error('User already has this sponsor')
      }

      // Check for circular reference
      const isCircular = await checkCircularReference(selectedUser.id, selectedSponsor.id)
      if (isCircular) {
        throw new Error('Circular reference detected - new sponsor is in user\'s downline')
      }

      // Perform sponsor change
      const changeResult = await performSponsorChange(selectedUser, selectedSponsor, currentSponsor)
      const bonus = changeResult?.sponsorBonus
      let resultType: 'success' | 'warning' = 'success'
      let resultText = `Successfully changed sponsor for ${selectedUser.username} to ${selectedSponsor.username}`
      if (bonus) {
        if (!bonus.ok) {
          resultType = 'warning'
          resultText += ` Package sponsor bonus was not moved (${bonus.error || 'unknown error'}).`
        } else if (Number(bonus.movedShares) > 0) {
          resultText += ` Package sponsor bonus moved: ${Number(bonus.movedShares)} shares across ${bonus.movedRows} row(s).`
        } else {
          resultText += ' No active package sponsor bonus shares to move.'
        }
        if (Number(bonus.consolidatedRows) > 0) {
          resultType = 'warning'
          resultText += ` ${bonus.consolidatedRows} sponsor bonus row(s) were already consolidated into the old sponsor's mixed bonus total and were not split.`
        }
      }

      setMessage({
        type: resultType,
        text: resultText
      })

      // Reload data BEFORE resetting form so we can verify
      console.log('🔄 Reloading referrals to verify change...')
      await loadReferrals()

      // Verify the change
      const updatedSponsor = getCurrentSponsor(selectedUser.id)
      console.log('🔍 Verification after reload:')
      console.log(`   User: ${selectedUser.username} (ID: ${selectedUser.id})`)
      console.log(`   New Sponsor in state: ${updatedSponsor?.referrer_username || 'NOT FOUND'}`)
      console.log(`   Expected: ${selectedSponsor.username}`)

      if (updatedSponsor?.referrer_id === selectedSponsor.id) {
        console.log('   ✅ Sponsor change verified in UI state!')
      } else {
        console.warn('   ⚠️ WARNING: Sponsor not updated in UI state!')
      }

      // Reset form
      setSelectedUser(null)
      setSelectedSponsor(null)
      setUserSearchTerm('')
      setSponsorSearchTerm('')
      setChangeReason('')
      setPurchaseInfo(null)

    } catch (error: any) {
      console.error('Error changing sponsor:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to change sponsor' })
    } finally {
      setLoading(false)
    }
  }

  const checkCircularReference = async (userId: number, newSponsorId: number, visited = new Set<number>()): Promise<boolean> => {
    if (visited.has(newSponsorId)) {
      return true
    }

    visited.add(newSponsorId)

    const sponsorReferral = referrals.find(ref => ref.referred_id === newSponsorId && ref.status === 'active')
    
    if (sponsorReferral && sponsorReferral.referrer_id === userId) {
      return true
    }

    if (sponsorReferral && sponsorReferral.referrer_id) {
      return await checkCircularReference(userId, sponsorReferral.referrer_id, visited)
    }

    return false
  }

  const performSponsorChange = async (user: User, newSponsor: User, currentSponsor?: ReferralRelationship) => {
    let sponsorBonus: { ok: boolean; error?: string; movedShares?: number; movedRows?: number; consolidatedRows?: number } | null = null
    const sessionId = getServerSessionId()

    console.log('🔄 Starting sponsor change...')
    console.log(`   User: ${user.username} (ID: ${user.id})`)
    console.log(`   New Sponsor: ${newSponsor.username} (ID: ${newSponsor.id})`)
    console.log(`   Current Sponsor: ${currentSponsor?.referrer_username || 'None'}`)

    if (!currentSponsor) {
      // No existing referral - create new one
      const newReferralCode = `${newSponsor.username}_${user.id}_${Date.now()}`

      console.log(`   Creating new referral with code: ${newReferralCode}...`)
      const response = await fetch('/api/admin/sponsor', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'create_referral',
          data: { newSponsorId: newSponsor.id, userId: user.id, referralCode: newReferralCode }
        })
      })
      const result = await response.json()
      if (!result.success) {
        console.error('❌ Error creating new referral:', result.error)
        throw new Error(result.error)
      }

      console.log('   ✅ New referral created')
    } else {
      // Update existing referral - just change the sponsor
      const newReferralCode = `${newSponsor.username}_${user.id}_${Date.now()}`

      console.log(`   Updating referral (ID: ${currentSponsor.id})...`)
      console.log(`   Changing referrer_id from ${currentSponsor.referrer_id} to ${newSponsor.id}`)

      const updateResponse = await fetch('/api/admin/sponsor', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'update_referral',
          data: { referralId: currentSponsor.id, newSponsorId: newSponsor.id, referralCode: newReferralCode }
        })
      })
      const updateResult = await updateResponse.json()
      if (!updateResult.success) {
        console.error('❌ Error updating referral:', updateResult.error)
        throw new Error(updateResult.error)
      }

      console.log('   ✅ Referral updated - sponsor changed')

      // CRITICAL: Transfer commissions from old sponsor to new sponsor
      console.log('💰 Transferring commissions from old sponsor to new sponsor...')

      const transferResponse = await fetch('/api/admin/sponsor', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'transfer_commissions',
          data: {
            userId: user.id,
            oldSponsorId: currentSponsor.referrer_id,
            newSponsorId: newSponsor.id,
            adminEmail: currentUser?.email || 'admin',
            reason: changeReason || 'Admin sponsor change'
          }
        })
      })
      const transferRes = await transferResponse.json()
      if (!transferRes.success) {
        console.error('❌ Error transferring commissions:', transferRes.error)
        throw new Error(`Commission transfer failed: ${transferRes.error}`)
      }

      const transferResult = transferRes.transferResult
      sponsorBonus = transferResult?.sponsor_bonus || null
      console.log('💰 Commission Transfer Result:', transferResult)

      if (!transferResult.success) {
        throw new Error(transferResult.message || 'Commission transfer failed')
      }

      // Show detailed transfer information
      const mlcUpdated = transferResult.mlc_updated || 0
      const spUpdated = transferResult.share_purchases_updated || 0
      const ctUpdated = transferResult.ct_updated || 0
      const balanceChanges = transferResult.balance_changes || []

      if (transferResult.partial_transfer) {
        setMessage({
          type: 'warning',
          text: `Sponsor changed with PARTIAL commission transfer. USDT: $${transferResult.usdt_transferred}/$${transferResult.total_usdt_requested}, Shares: ${transferResult.shares_transferred}/${transferResult.total_shares_requested}. Old sponsor had insufficient balance.`
        })
      } else if (transferResult.usdt_transferred == 0 && transferResult.shares_transferred == 0) {
        setMessage({
          type: 'info',
          text: `Sponsor changed successfully. No commissions found to transfer (user may not have any purchases yet).`
        })
      } else {
        const details = [
          `USDT: $${Number(transferResult.usdt_transferred).toFixed(2)}`,
          `Shares: ${Number(transferResult.shares_transferred).toFixed(6)}`,
          `MLC records: ${mlcUpdated}`,
          `Share purchases: ${spUpdated}`,
          ctUpdated > 0 ? `Legacy txns: ${ctUpdated}` : null
        ].filter(Boolean).join(' | ')

        const levelDetails = balanceChanges.length > 0
          ? ' — Levels: ' + balanceChanges.map((bc: { level: number; from_user_id: number; to_user_id: number; usdt: number; shares: number }) =>
              `L${bc.level}: $${Number(bc.usdt).toFixed(2)} + ${Number(bc.shares).toFixed(6)}sh (${bc.from_user_id}→${bc.to_user_id})`
            ).join(', ')
          : ''

        setMessage({
          type: 'success',
          text: `Commission transfer complete. ${details}${levelDetails}`
        })
      }

      // Send notifications to both sponsors
      console.log('📧 Sending notifications to sponsors...')
      try {
        await notifySponsorsOfCommissionTransfer({
          userId: user.id,
          oldSponsorId: currentSponsor.referrer_id,
          newSponsorId: newSponsor.id,
          usdtTransferred: transferResult.usdt_transferred,
          sharesTransferred: transferResult.shares_transferred,
          reason: changeReason || 'Admin sponsor change',
          adminEmail: currentUser?.email || 'admin'
        })
        console.log('✅ Notifications sent successfully')
      } catch (notifError) {
        console.error('⚠️ Failed to send notifications (non-critical):', notifError)
        // Don't throw - notifications are non-critical
      }
    }

    // Create audit log for sponsor change
    const auditDetails = {
      user_id: user.id,
      user_username: user.username,
      old_sponsor_id: currentSponsor?.referrer_id || null,
      old_sponsor_username: currentSponsor?.referrer_username || null,
      new_sponsor_id: newSponsor.id,
      new_sponsor_username: newSponsor.username,
      reason: changeReason || 'Admin sponsor change',
      admin_user: currentUser?.email || 'admin',
      timestamp: new Date().toISOString()
    }

    // Log audit via server-side API (not direct DB access from client)
    await fetch('/api/admin/sponsor', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionId ? { 'X-Session-Id': sessionId } : {})
      },
      body: JSON.stringify({
        operation: 'audit_log',
        data: {
          adminUsername: currentUser?.email || 'admin',
          action: 'SPONSOR_CHANGE',
          targetType: 'user_sponsor',
          targetId: user.id.toString(),
          details: auditDetails
        }
      })
    })

    return { sponsorBonus }
  }



  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Sponsor Management</h2>
        <div className="text-sm text-white">
          Total Users: {users.length} | Active Referrals: {referrals.length}
        </div>
      </div>

      {message && (
        <div className={`p-4 rounded-lg border ${
          message.type === 'success' ? 'bg-green-900/20 border-green-500/30 text-green-300' :
          message.type === 'error' ? 'bg-red-900/20 border-red-500/30 text-red-300' :
          'bg-blue-900/20 border-blue-500/30 text-blue-300'
        }`}>
          {message.text}
        </div>
      )}

      {/* Sponsor Change Form */}
      <div className="glass-card p-6">
        <h3 className="text-xl font-semibold text-white mb-4">Change User Sponsor</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* User Selection Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Search & Select User
            </label>
            <div className="relative" ref={userDropdownRef}>
              <input
                type="text"
                placeholder="Search by username, email, or name..."
                value={userSearchTerm}
                onChange={(e) => {
                  setUserSearchTerm(e.target.value)
                  setShowUserDropdown(true)
                  if (!e.target.value) {
                    setSelectedUser(null)
                    setPurchaseInfo(null)
                  }
                }}
                onFocus={() => setShowUserDropdown(true)}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                autoComplete="off"
              />

              {showUserDropdown && (
                <div className="absolute z-20 w-full mt-1 max-h-48 overflow-y-auto border border-gray-600 rounded-lg bg-gray-800 shadow-xl backdrop-blur-sm">
                  {getFilteredUsers().length > 0 ? (
                    <>
                      {getFilteredUsers().length > 5 && (
                        <div className="px-3 py-2 bg-gray-700 text-sm text-gray-300 border-b border-gray-600">
                          {getFilteredUsers().length} users found
                        </div>
                      )}
                      {getFilteredUsers().map(user => {
                      const currentSponsor = getCurrentSponsor(user.id)
                      const isSelected = selectedUser?.id === user.id
                      return (
                        <div
                          key={user.id}
                          onClick={() => handleUserSelect(user)}
                          className={`p-3 cursor-pointer hover:bg-gray-700 border-b border-gray-700 last:border-b-0 transition-colors ${
                            isSelected ? 'bg-yellow-500/20 border-yellow-500/30' : ''
                          }`}
                        >
                          <div className="font-medium text-white">{user.username} <span className="text-yellow-400">(ID: {user.id})</span></div>
                          <div className="text-sm text-white">{user.full_name || 'No Name'}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                          <div className="text-sm text-gray-500">
                            Current Sponsor: {currentSponsor?.referrer_username || 'None'}
                          </div>
                        </div>
                      )
                    })}
                    </>
                  ) : (
                    <div className="p-3 text-white text-sm">No users found</div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            {/* Selected User Display */}
            {selectedUser && (
              <div className="p-4 bg-gray-800 rounded-lg border border-gray-600">
                <h4 className="font-medium text-white mb-2">Selected User</h4>
                <div className="text-sm text-gray-300">
                  <div><strong>ID:</strong> <span className="text-yellow-400">{selectedUser.id}</span></div>
                  <div><strong>Username:</strong> {selectedUser.username}</div>
                  <div><strong>Email:</strong> {selectedUser.email}</div>
                  <div><strong>Name:</strong> {selectedUser.full_name || 'N/A'}</div>
                  <div><strong>Current Sponsor:</strong> {getCurrentSponsor(selectedUser.id)?.referrer_username || 'None'}</div>
                </div>
              </div>
            )}

            {/* Purchase History Warning */}
            {selectedUser && purchaseCheckLoading && (
              <div className="p-3 bg-gray-700/50 rounded-lg border border-gray-600 animate-pulse">
                <span className="text-sm text-gray-400">Checking purchase history...</span>
              </div>
            )}
            {selectedUser && purchaseInfo && purchaseInfo.hasPurchases && (
              <div className={`p-4 rounded-lg border ${
                purchaseInfo.commissionsToOtherSponsors > 0
                  ? 'bg-amber-900/20 border-amber-500/30'
                  : 'bg-blue-900/20 border-blue-500/30'
              }`}>
                <div className={`font-medium text-sm mb-1 ${
                  purchaseInfo.commissionsToOtherSponsors > 0 ? 'text-amber-300' : 'text-blue-300'
                }`}>
                  {purchaseInfo.commissionsToOtherSponsors > 0 ? '⚠️ Purchase History Warning' : 'ℹ️ Purchase History'}
                </div>
                <div className="text-sm text-gray-300 space-y-1">
                  <div>This user has <strong>{purchaseInfo.purchaseCount}</strong> commission record(s) totalling <strong>${purchaseInfo.totalUsdt.toFixed(2)}</strong> USDT + <strong>{purchaseInfo.totalShares.toFixed(4)}</strong> shares.</div>
                  {purchaseInfo.commissionsToOtherSponsors > 0 && (
                    <div className="text-amber-300">
                      {purchaseInfo.commissionsToOtherSponsors} commission(s) were credited to other sponsor(s): {purchaseInfo.otherSponsorNames.map(s => s.username).join(', ')}.
                      Changing sponsor will transfer these commissions to the new sponsor.
                    </div>
                  )}
                  {!getCurrentSponsor(selectedUser.id) && (
                    <div className="text-amber-300 mt-1">
                      ⚠️ This user has <strong>no current sponsor</strong> but has past purchases. Commissions may have gone to the system account (AUREUS). The transfer RPC will attempt to re-attribute them.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Sponsor Selection Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Select New Sponsor
              </label>
              <div className="relative" ref={sponsorDropdownRef}>
                <input
                  type="text"
                  placeholder={selectedUser ? "Search for new sponsor..." : "Select a user first"}
                  value={sponsorSearchTerm}
                  onChange={(e) => {
                    setSponsorSearchTerm(e.target.value)
                    setShowSponsorDropdown(true)
                    if (!e.target.value) {
                      setSelectedSponsor(null)
                    }
                  }}
                  onFocus={() => selectedUser && setShowSponsorDropdown(true)}
                  disabled={!selectedUser}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  autoComplete="off"
                />

                {showSponsorDropdown && selectedUser && (
                  <div className="absolute z-20 w-full mt-1 max-h-48 overflow-y-auto border border-gray-600 rounded-lg bg-gray-800 shadow-xl backdrop-blur-sm">
                    {getFilteredSponsors().length > 0 ? (
                      <>
                        {getFilteredSponsors().length > 5 && (
                          <div className="px-3 py-2 bg-gray-700 text-sm text-gray-300 border-b border-gray-600">
                            {getFilteredSponsors().length} eligible sponsors found
                          </div>
                        )}
                        {getFilteredSponsors().map(sponsor => {
                        const isSelected = selectedSponsor?.id === sponsor.id
                        return (
                          <div
                            key={sponsor.id}
                            onClick={() => handleSponsorSelect(sponsor)}
                            className={`p-3 cursor-pointer hover:bg-gray-700 border-b border-gray-700 last:border-b-0 transition-colors ${
                              isSelected ? 'bg-green-500/20 border-green-500/30' : ''
                            }`}
                          >
                            <div className="font-medium text-white">{sponsor.username} <span className="text-green-400">(ID: {sponsor.id})</span></div>
                            <div className="text-sm text-white">{sponsor.full_name || 'No Name'}</div>
                            <div className="text-sm text-gray-500">{sponsor.email}</div>
                          </div>
                        )
                      })}
                      </>
                    ) : (
                      <div className="p-3 text-white text-sm">No eligible sponsors found</div>
                    )}
                  </div>
                )}
              </div>

              {selectedSponsor && (
                <div className="mt-2 p-3 bg-green-900/20 border border-green-500/30 rounded-lg">
                  <div className="text-sm text-green-300">
                    <strong>Selected Sponsor:</strong> {selectedSponsor.username} (ID: {selectedSponsor.id}) - {selectedSponsor.full_name || 'No Name'}
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Reason for Change (Optional)
              </label>
              <textarea
                placeholder="Enter reason for sponsor change..."
                value={changeReason}
                onChange={(e) => setChangeReason(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              />
            </div>

            <button
              onClick={handleChangeSponsor}
              disabled={loading || !selectedUser || !selectedSponsor}
              className="w-full py-3 px-4 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-bold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin"></div>
                  Changing Sponsor...
                </div>
              ) : (
                'Change Sponsor'
              )}
            </button>

            {(!selectedUser || !selectedSponsor) && (
              <div className="text-sm text-white text-center mt-2">
                {!selectedUser ? 'Please select a user first' : 'Please select a sponsor'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Current Referral Relationships */}
      <div className="glass-card p-6">
        <h3 className="text-xl font-semibold text-white mb-4">Current Referral Relationships</h3>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-600">
                <th className="text-left py-3 px-4 text-gray-300">Referred User</th>
                <th className="text-left py-3 px-4 text-gray-300">Sponsor</th>
                <th className="text-left py-3 px-4 text-gray-300">Commission Rate</th>
                <th className="text-left py-3 px-4 text-gray-300">Total Commission</th>
                <th className="text-left py-3 px-4 text-gray-300">Created</th>
              </tr>
            </thead>
            <tbody>
              {referrals.slice(0, 20).map(referral => (
                <tr key={referral.id} className="border-b border-gray-700 hover:bg-gray-800/50">
                  <td className="py-3 px-4 text-white">
                    <div>{referral.referred_username}</div>
                    <div className="text-sm text-white">{referral.referred_full_name}</div>
                  </td>
                  <td className="py-3 px-4 text-white">
                    <div>{referral.referrer_username}</div>
                    <div className="text-sm text-white">{referral.referrer_full_name}</div>
                  </td>
                  <td className="py-3 px-4 text-gray-300">{referral.commission_rate}%</td>
                  <td className="py-3 px-4 text-gray-300">${referral.total_commission.toFixed(2)}</td>
                  <td className="py-3 px-4 text-white">
                    {new Date(referral.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {referrals.length > 20 && (
          <div className="mt-4 text-center text-white">
            Showing 20 of {referrals.length} relationships
          </div>
        )}
      </div>
    </div>
  )
}
